import { HttpClient, HttpHeaders, HttpResponse, HttpXsrfTokenExtractor } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AsyncSubject, Subject, map, } from 'rxjs';
import { MEErrorService } from 'src/app/common/error/me-error.service';
import { MEAPIHandler, MEBasicAPIHandler, MEDownloadAPIHandler, SystemError } from './me-api-handler';
import { MEAPIResponse, MEBasicAPIResponseHandler, MEBasicAPIResponse, MEEntityAPIResponseHandler, MEDownloadAPIResponseHandler } from './me-api-response';
import { TranslateUtilityService } from 'src/app/system/utility/translation-utility.service';
import { CapacitorHttp, HttpOptions, HttpHeaders as CapacitorHttpHeaders } from '@capacitor/core';
import { PlatformService } from 'src/app/system/utility/platform.service';
import { FileSystemService } from 'src/app/system/utility/filesystem.service';
import { DeviceInfoService } from 'src/app/system/utility/device-info.service';
import { EnvironmentService } from 'src/app/system/utility/environment.service';
import { AppService } from 'src/app/system/utility/app.service';
import { LoadingService } from '../loading/loading.service';
import { Buffer } from 'buffer';
import { MEApiUtility } from './me-api.utility';
import { UserSessionService } from '../user-session/user-session.service';

@Injectable({
  providedIn: 'root'
})
export class MEAPIService {

  public static readonly NO_RESPONSE_HANDLER = [];
  public static readonly BASE_URL: string = "/api/v1";

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly srvError: MEErrorService,
    private readonly translate: TranslateUtilityService,
    private readonly tokenExtractor: HttpXsrfTokenExtractor,
    private readonly srvFileSystem: FileSystemService,
    private readonly srvLoading: LoadingService,
    private readonly srvUserSession: UserSessionService,
  ) {
  }

  public Post<ResponseType>(url: string,
    bean: any,
    handlers: MEEntityAPIResponseHandler<ResponseType>[],
    systemError?: SystemError
  ): AsyncSubject<boolean> {

    if (!this.srvError.systemAvailable.value) {
      let ret: AsyncSubject<boolean> = new AsyncSubject<boolean>();
      ret.next(false);
      ret.complete();
      return ret;
    }

    let handler = new MEAPIHandler<ResponseType>(handlers, this.srvError, this.router, systemError, this.translate, this.srvUserSession);

    this.http
      .post<MEAPIResponse<ResponseType>>(url, bean)
      .pipe(
        map((res: MEAPIResponse<ResponseType>) => {
          return res;
        })
      ).subscribe(handler);

    return handler.result$;
  }

  public BasicPost(url: string,
    bean: any,
    handlers: MEBasicAPIResponseHandler[],
    systemError?: SystemError
  ): AsyncSubject<boolean> {

    if (!this.srvError.systemAvailable.value) {
      let ret: AsyncSubject<boolean> = new AsyncSubject<boolean>();
      ret.next(false);
      ret.complete();

      return ret;
    }
    let handler = new MEBasicAPIHandler(handlers, this.srvError, this.router, systemError, this.translate, this.srvUserSession);

    this.http
      .post<MEBasicAPIResponse>(url, bean)
      .pipe(
        map((res: MEBasicAPIResponse) => {
          return res;
        })
      ).subscribe(handler);

    return handler.result$;
  }

  public Get<ResponseType>(
    url: string,
    handlers: MEEntityAPIResponseHandler<ResponseType>[],
    systemError?: SystemError
  ): AsyncSubject<boolean> {

    if (!this.srvError.systemAvailable.value) {
      let ret: AsyncSubject<boolean> = new AsyncSubject<boolean>();
      ret.next(false);
      ret.complete();
      return ret;
    }

    let handler = new MEAPIHandler<ResponseType>(handlers, this.srvError, this.router, systemError, this.translate, this.srvUserSession);

    this.http
      .get<MEAPIResponse<ResponseType>>(url)
      .pipe(
        map((res: MEAPIResponse<ResponseType>) => {
          return res;
        })
      ).subscribe(handler);

    return handler.result$
  }

  public BasicGet(url: string,
    handlers: MEBasicAPIResponseHandler[],
    systemError?: SystemError
  ): AsyncSubject<boolean> {

    if (!this.srvError.systemAvailable.value) {
      let ret: AsyncSubject<boolean> = new AsyncSubject<boolean>();
      ret.next(false);
      ret.complete();

      return ret;

    }
    let handler = new MEBasicAPIHandler(handlers, this.srvError, this.router, systemError, this.translate, this.srvUserSession);

    this.http.get(url, { observe: 'response' })
      .pipe(
        map((res: HttpResponse<any>) => {
          let body: MEBasicAPIResponse = res.body;
          return body;
        })).subscribe(handler);

    return handler.result$
  }

  public BasicFormPost<ResponseType>(
    url: string,
    bean: any,
    files: File[],
    handlers: MEBasicAPIResponseHandler[],
    systemError?: SystemError
  ): AsyncSubject<boolean> {

    if (!this.srvError.systemAvailable.value) {
      let ret: AsyncSubject<boolean> = new AsyncSubject<boolean>();
      ret.next(false);
      ret.complete();

      return ret;

    }
    let handler = new MEBasicAPIHandler(handlers, this.srvError, this.router, systemError, this.translate, this.srvUserSession);
    let formData: FormData = new FormData();
    let beanBlob: Blob = new Blob([JSON.stringify(bean)], { type: 'application/json' });

    formData.append('json', beanBlob);
    files.forEach((file) => {
      formData.append('files', file);
    });

    // Perform Posting
    this.postFormData(url, formData, handler);

    return handler.result$;

  }

  public FormDataPost<ResponseType>(
    url: string, formData: FormData,
    handlers: MEEntityAPIResponseHandler<ResponseType>[],
    systemError?: SystemError
  ): AsyncSubject<boolean> {

    if (!this.srvError.systemAvailable.value) {
      let ret: AsyncSubject<boolean> = new AsyncSubject<boolean>();
      ret.next(false);
      ret.complete();

      return ret;

    }
    let handler = new MEAPIHandler(handlers, this.srvError, this.router, systemError, this.translate, this.srvUserSession);

    // Handle Posting
    this.postFormData(url, formData, handler);

    return handler.result$;

  }


  public DownloadPost(url: string, bean: any, handlers: MEDownloadAPIResponseHandler[]): AsyncSubject<boolean> {

    if (!this.srvError.systemAvailable.value) {
      let ret: AsyncSubject<boolean> = new AsyncSubject<boolean>();
      ret.next(false);
      ret.complete();
      return ret;
    }

    let handler = new MEDownloadAPIHandler(handlers, this.srvError);

    if (PlatformService.isBrowser()) {
      this.http.post(url, bean, { responseType: "arraybuffer", observe: 'response' }).subscribe(handler);
    }
    else {
      // Even "Native HTTP" plugin does not support responseType: 'arraybuffer', need a workaround to cater
      // https://github.com/ionic-team/capacitor/issues/6126

      let options: HttpOptions = {
        url: this.appendUrl(url),
        headers: this.getCapacitorHttpHeaders("application/json"),
        responseType: 'arraybuffer',
        data: bean
      };

      CapacitorHttp.post(options).then((response) => {
        MEApiUtility.init(this.srvError);

        if (MEApiUtility.isHttpError(response.status)) {
          MEApiUtility.showHttpErrDialog(response.status);
          return;
        }

        let buf = Buffer.from(response.data, 'base64');
        let body: ArrayBuffer = buf.buffer as unknown as ArrayBuffer;
        let headers = new HttpHeaders();
        for (let key in response.headers) {
          headers = headers.set(key, response.headers[key]);
        }

        let result = new HttpResponse<ArrayBuffer>({ body, headers });
        handler.next(result);
      }).catch((error)=> {
        MEApiUtility.showCapacitorErrDialog(error);
      });
    }
    return handler.result$;
  }

  public DownloadGet(url: string, filename: string, content: Subject<Blob | null> | null = null, options: {useFallback: boolean} = {useFallback: false}) : AsyncSubject<boolean> { 
    let ret: AsyncSubject<boolean> = new AsyncSubject<boolean>();

    if (!this.srvError.systemAvailable.value) {
      ret.next(false);
      ret.complete();
    } else {

      this.http.get(url, { responseType: 'blob' }).subscribe({
        next: (blob: Blob): void => {
          if (content) {
            content.next(blob);
          } else {
            this.srvFileSystem.downloadBlob(filename, blob);
          }
          ret.next(true);
          ret.complete();
        },
        error: (_err: any): void => {
          if (options.useFallback) content?.next(null);
          else this.srvError.showDialog(this.srvError.SERVER_TITLE, [this.translate.instant("ERROR_DOWNLOAD_FILE")]);
          ret.next(false);
          ret.complete();
        }
      });
    }
    return ret;
  }

  public getCapacitorHttpHeaders(contentType: string = ''): CapacitorHttpHeaders {
    let map = this.getMEHeaders();
    if (contentType) {
      map.set('Content-Type', contentType);
    }
    let json = JSON.stringify(Object.fromEntries(map))
    return JSON.parse(json);
  }

  public getHttpHeaders(): HttpHeaders {
    let map = this.getMEHeaders();
    let json = JSON.stringify(Object.fromEntries(map))
    return new HttpHeaders(JSON.parse(json));
  }

  private getMEHeaders(): Map<string, string> {
    let headers = new Map<string, string>();
    headers.set('X-XSRF-TOKEN', this.tokenExtractor.getToken() ?? '');
    headers.set('appname', AppService.getAppName());
    headers.set('appplatform', PlatformService.getAppPlatform());
    headers.set('appversion', AppService.getAppVersion());
    headers.set('brandname',  DeviceInfoService.getDeviceBrandName());
    headers.set('deviceid', DeviceInfoService.getDeviceId());
    headers.set('devicetype', PlatformService.getDeviceType());
    headers.set('modelname', DeviceInfoService.getDeviceModel());
    return headers;
  }

  public appendUrl(url: string): string {
    if (!url.startsWith("/api")) return url;
    if (PlatformService.isMobile()) return EnvironmentService.getBaseUrl() + url;
    return url;
  }

  /** PostFormData
      Remark from Capacitor HTTP Plugin: https://capacitorjs.com/docs/apis/http#httpoptions
         On Android and iOS, data can only be a string or a JSON. FormData, Blob, ArrayBuffer, and other complex types are only directly supported on web or through enabling CapacitorHttp in the config and using the patched window.fetch or XMLHttpRequest.
         If you need to send a complex type, you should serialize the data to base64 and set the headers["Content-Type"] and dataType attributes accordingly.
      Instead of serializing the data to base64, we can use the FormData object and send it via the fetch API.
  */
  private postFormData(url: string, formData: FormData, handler: MEAPIHandler<any> | MEBasicAPIHandler) {

    if (PlatformService.isBrowser()) {
      this.http
        .post<MEAPIResponse<ResponseType>>(url, formData)
        .pipe(
          map((res: MEAPIResponse<ResponseType>) => {
            return res;
          })
        ).subscribe(handler);
    }
    else {
      this.srvLoading.httpStart();
      fetch(
        this.appendUrl(url), {
        method: 'POST',
        headers: this.getCapacitorHttpHeaders(),
        body: formData
      })
        .then(response => response.json())
        .then(result => {
          handler.next(result);
          handler.complete();
          this.srvLoading.httpEnd();
        })
    }
  }
}
